import React, { useEffect, useState } from 'react';
import { Record, useGetIdentity, useTranslate } from 'react-admin';
import {
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import SectionTitle from '../SectionTitle';
import ReportLink from '../ReportLink';
import { TestNames } from '../../constants/test/testNames';
import { getLabReportUrl, getUserTestHistory } from '../../utils/requests';
import TestHistoryItem from '../../models/testHistoryItem';
import './styles.scss';

const getTestName = (type: string) =>
  TestNames[type as keyof typeof TestNames] || type;

// dd.mm.yyyy, the same format as the date of birth above it.
const formatResultsDate = (resultsDate: string | null) =>
  resultsDate
    ? new Date(resultsDate).toLocaleDateString('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '-';

// The person's other completed tests, so the doctor can compare while reviewing.
// Admin-only: the backend refuses the request for anyone else.
const TestHistory = ({ record }: { record: Record | undefined }) => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const isAdmin = Boolean(identity?.userRoles?.includes('admin'));
  const userId: string | undefined = record?.userId;
  // Fertility's getOne returns the raw test doc, which has `code` but no `id`.
  const currentTestId = record?.code ?? record?.id;
  const [history, setHistory] = useState<TestHistoryItem[]>();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!isAdmin || !userId) {
      return undefined;
    }
    let isCancelled = false;
    setHistory(undefined);
    setHasError(false);
    getUserTestHistory(userId)
      .then((items) => {
        if (!isCancelled) setHistory(items);
      })
      .catch(() => {
        if (!isCancelled) setHasError(true);
      });
    return () => {
      isCancelled = true;
    };
  }, [isAdmin, userId]);

  if (!isAdmin || !userId) {
    return null;
  }

  const otherTests = history?.filter((item) => item.id !== currentTestId);

  const renderContent = () => {
    if (hasError) {
      return (
        <p className="testHistory__message">
          {translate('testResults.testHistoryError')}
        </p>
      );
    }
    if (!otherTests) {
      return <LinearProgress className="testHistory__loading" />;
    }
    if (otherTests.length === 0) {
      return (
        <p className="testHistory__message">
          {translate('testResults.testHistoryEmpty')}
        </p>
      );
    }
    return (
      <Table size="small" className="testHistory__table">
        <TableHead>
          <TableRow>
            <TableCell className="testHistory__column--narrow">
              {translate('testResults.testType')}
            </TableCell>
            <TableCell className="testHistory__column--narrow">
              {translate('testResults.resultsDate')}
            </TableCell>
            <TableCell>{translate('testResults.labReport')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {otherTests.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{getTestName(item.type)}</TableCell>
              <TableCell>{formatResultsDate(item.resultsDate)}</TableCell>
              <TableCell>
                {item.hasLabReport ? (
                  <ReportLink
                    fetchUrl={() => getLabReportUrl(item.id)}
                    linkText="testResults.openLabReport"
                    errorMessage="testResults.labReportError"
                  />
                ) : (
                  '-'
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="testHistory">
      <SectionTitle title={translate('testResults.testHistory')} />
      {renderContent()}
      <Divider className="divider" />
    </div>
  );
};

export default TestHistory;
